import { useState } from "react";
import {
  Modal,
  Box,
  IconButton,
  Typography,
  Divider,
  TextField,
  InputAdornment,
  Button,
  CircularProgress,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import PasswordIcon from "@mui/icons-material/Password";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { globalStyle } from "../../global/Utils";

const ChangePasswordModal = ({
  loader,
  openModalPassword,
  setOpenPasswordModal,
  isFromMui = false,
  handleSubmit,
}) => {
  // Old Password State
  const [showPassword, setShowPassword] = useState(false);
  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const [password, setPassword] = useState("");
  const handlePasswordChangeValidation = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
  };
  // New Password
  const [showNewPassword, setShowNewPassword] = useState(false);
  const handleClickShowNewPassword = () => setShowNewPassword((show) => !show);
  const [newPasswordErrors, setNewPasswordErrors] = useState([]);
  const [newPassword, setNewPassword] = useState("");
  const validateNewPassword = (password) => {
    const errors = [];
    if (password.length < 8) errors.push("Must be at least 8 characters");
    if (!/[A-Z]/.test(password))
      errors.push("Must contain an uppercase letter");
    if (!/[a-z]/.test(password)) errors.push("Must contain a lowercase letter");
    if (!/[0-9]/.test(password)) errors.push("Must contain a number");
    if (!/[!@#$%^&*]/.test(password))
      errors.push("Must contain a special character");
    return errors;
  };
  const handleNewPasswordChangeValidation = (e) => {
    const newPassword = e.target.value;
    setNewPassword(newPassword);

    // Validate the password and set errors
    const errors = validateNewPassword(newPassword);
    setNewPasswordErrors(errors);

    validatePasswords(newPassword, cPassword);
  };
  // Confirm Password
  const [showCPassword, setShowCPassword] = useState(false);
  const [errorPassword, setErrorPassword] = useState(false);
  const handleClickShowCPassword = () => setShowCPassword((show) => !show);
  const [helperTextPassword, setHelperTextPassword] = useState("");
  const [cPassword, setCPassword] = useState("");
  const handleConfirmPasswordChange = (event) => {
    setCPassword(event.target.value);
    validatePasswords(newPassword, event.target.value);
  };
  const validatePasswords = (password, confirmPassword) => {
    if (password && confirmPassword && password !== confirmPassword) {
      setErrorPassword(true);
      setHelperTextPassword("Passwords do not match");
    } else {
      setErrorPassword(false);
      setHelperTextPassword("");
    }
  };
  const handleClosePasswordModal = () => {
    setOpenPasswordModal(false);
    setPassword("");
    setNewPassword("");
    setCPassword("");
    setNewPasswordErrors([]);
    setErrorPassword(false);
    setHelperTextPassword("");
  };
  const handleSubmitChangePassword = () => {
    if (isFromMui) {
      handleSubmit(
        {
          oldPass: password,
          newPass: cPassword,
        },
        handleClosePasswordModal,
      );
    } else {
      handleSubmit(
        {
          newPass: cPassword,
        },
        handleClosePasswordModal,
      );
    }
  };
  if (!openModalPassword) return null;
  return (
    <Modal
      open={openModalPassword}
      onClose={handleClosePasswordModal}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 400,
          bgcolor: "background.paper",
          boxShadow: 24,
          p: "24px",
          borderRadius: 2,
        }}
      >
        <IconButton
          onClick={handleClosePasswordModal}
          sx={{
            float: "right",
            color: "black",
          }}
        >
          <CancelIcon sx={{ fontSize: "28px" }} />
        </IconButton>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            m: 2,
            ml: { xs: 1, md: 3, lg: 3 },
          }}
        >
          <PasswordIcon fontSize="medium" sx={{ mr: 1 }} />
          <Typography
            variant="subtitle1"
            fontFamily={globalStyle.fontFamilyParagraph}
            fontSize={globalStyle.fontSize}
          >
            Change Password
          </Typography>
        </Box>
        <Divider />
        <Box>
          <Box component="form" noValidate sx={{ mt: 1, width: "100%" }}>
            {isFromMui && (
              <>
                <Box display="flex" gap={2} mb={1}>
                  <Typography
                    component="h1"
                    variant="h5"
                    sx={{
                      fontSize: globalStyle.fontSize,
                      fontFamily: globalStyle.fontFamily,
                    }}
                  >
                    Old Password
                  </Typography>
                </Box>
                <Box display="flex" gap={2} mb={1}>
                  <TextField
                    margin="dense"
                    required
                    fullWidth
                    name="password"
                    size="small"
                    placeholder="********"
                    type={showPassword ? "text" : "password"}
                    id="password"
                    sx={{ borderRadius: globalStyle.borderRadius }}
                    onChange={(e) => handlePasswordChangeValidation(e)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleClickShowPassword}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    autoComplete="off"
                  />
                </Box>
              </>
            )}
            <Box display="flex" gap={2} mb={1}>
              <Typography
                component="h1"
                variant="h5"
                sx={{
                  fontSize: globalStyle.fontSize,
                  fontFamily: globalStyle.fontFamily,
                }}
              >
                New Password
              </Typography>
            </Box>
            <Box display="flex" gap={2} mb={1}>
              <TextField
                margin="dense"
                required
                id="password"
                name="password"
                type={showNewPassword ? "text" : "password"}
                size="small"
                placeholder="********"
                autoFocus
                variant="outlined"
                fullWidth
                error={newPasswordErrors.length > 0}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleClickShowNewPassword}
                        edge="end"
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                onChange={(e) => handleNewPasswordChangeValidation(e)}
                sx={{ borderRadius: globalStyle.borderRadius }}
              />
            </Box>
            <Box sx={{ mt: 2, mb: 2 }}>
              {newPasswordErrors.length > 0 &&
                newPasswordErrors.map((error, index) => (
                  <Typography key={index} color="error" variant="body2">
                    {error}
                  </Typography>
                ))}
            </Box>
            <Box display="flex" gap={2} mb={1}>
              <Typography
                component="h1"
                variant="h5"
                sx={{
                  fontSize: globalStyle.fontSize,
                  fontFamily: globalStyle.fontFamily,
                }}
              >
                Confirm Password
              </Typography>
            </Box>
            <Box display="flex" gap={2} mb={1}>
              <TextField
                margin="dense"
                required
                fullWidth
                size="small"
                id="cPassword"
                type={showCPassword ? "text" : "password"}
                name="cPassword"
                placeholder="********"
                autoFocus
                error={errorPassword} // Sets error state for confirm password field
                helperText={helperTextPassword}
                variant="outlined"
                onChange={(e) => handleConfirmPasswordChange(e)}
                sx={{ borderRadius: globalStyle.borderRadius }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleClickShowCPassword} edge="end">
                        {showCPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>
          <Box
            display="flex"
            flexDirection={{ xs: "column", md: "row" }}
            gap={2}
            sx={{
              marginX: 16,
              paddingTop: "4px",
              width: "30%",
            }}
          >
            <Button
              type="button"
              fullWidth
              variant="contained"
              disabled={
                (isFromMui && password === "") ||
                cPassword === "" ||
                errorPassword ||
                newPasswordErrors.length > 0 ||
                newPassword === "" ||
                loader
              }
              sx={{
                mt: 1,
                mb: 1,
                bgcolor: globalStyle.buttonColor,
                fontFamily: globalStyle.fontFamilyButton,
                fontSize: globalStyle.fontSize,
              }}
              onClick={() => handleSubmitChangePassword()}
            >
              {loader ? (
                <CircularProgress color="blue" size={24} />
              ) : (
                "Confirm"
              )}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default ChangePasswordModal;
